"use client";

import { useState } from "react";
import { useRouter } from "next/navigation";
import { useMediaQuery } from "@/hooks/use-media-query";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Drawer, DrawerContent, DrawerHeader, DrawerTitle, DrawerDescription } from "@/components/ui/drawer";
import LoginForm from "./login-form";
import RegisterForm from "./register-form";
import { trackAction } from "@/lib/track-action";

type AuthDialogProps = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  redirectTo?: string;
  onSuccess?: () => void;
  /** Show a "Continuar como invitado" option. Only for catalog screens. */
  showGuestOption?: boolean;
  onGuestContinue?: () => void;
  /** When false, the dialog cannot be dismissed (no X, no outside click, no escape). */
  dismissable?: boolean;
  isInitiallyOnRegistrationView?: boolean;
};

export default function AuthDialog({
  open,
  onOpenChange,
  redirectTo,
  onSuccess,
  showGuestOption = false,
  onGuestContinue,
  dismissable = true,
  isInitiallyOnRegistrationView = false,
}: AuthDialogProps) {
  const [view, setView] = useState<"login" | "register">(isInitiallyOnRegistrationView ? "register" : "login");
  const [prefillEmail, setPrefillEmail] = useState("");
  const [prefillCellphone, setPrefillCellphone] = useState("");
  const [prefillPassword, setPrefillPassword] = useState("");
  const isDesktop = useMediaQuery("(min-width: 768px)");
  const router = useRouter();

  const handleClose = (nextOpen: boolean) => {
    if (!dismissable) return;
    onOpenChange(nextOpen);
    if (!nextOpen) setView("login");
  };

  const handleSuccess = () => {
    handleClose(false);
    if (onSuccess) {
      onSuccess();
    } else if (redirectTo) {
      router.push(redirectTo);
    }
  };

  const preventClose = (e: Event) => e.preventDefault();

  const guestButton = showGuestOption && onGuestContinue && (
    <div className="text-center mt-4">
      <button
        type="button"
        onClick={() => { trackAction("CONTINUE-AS-GUEST", view); onGuestContinue(); }}
        className="text-sm text-teal-600 hover:text-teal-700 cursor-pointer font-medium"
      >
        Continuar como invitado
      </button>
    </div>
  );

  const handleLoginFailed = (emailOrPhone: string, password: string) => {
    const isPhone = /^\d+$/.test(emailOrPhone.trim());
    setPrefillEmail(isPhone ? "" : emailOrPhone);
    setPrefillCellphone(isPhone ? emailOrPhone : "");
    setPrefillPassword(password);
    trackAction("GO-TO-REGISTRATION");
    setView("register");
  };

  const content = (extraContent?: React.ReactNode) => view === "login" ? (
    <LoginForm
      onSuccess={handleSuccess}
      onSwitchToRegister={() => setView("register")}
      onLoginFailed={handleLoginFailed}
      extraContent={extraContent}
    />
  ) : (
    <RegisterForm
      onSuccess={handleSuccess}
      onSwitchToLogin={() => setView("login")}
      extraContent={extraContent}
      initialEmail={prefillEmail}
      initialCellphone={prefillCellphone}
      initialPassword={prefillPassword}
    />
  );

  if (isDesktop || true) {
    return (
      <Dialog open={open} onOpenChange={handleClose}>
        <DialogContent
          showCloseButton={dismissable}
          onPointerDownOutside={dismissable ? undefined : preventClose}
          onEscapeKeyDown={dismissable ? undefined : preventClose}
          className="sm:max-w-md max-h-[85vh] flex flex-col overflow-hidden"
        >
          <DialogHeader>
            <DialogTitle className="text-2xl -mt-2 -mb-2">
              {view === "login" ? "Iniciar sesión" : "Crear cuenta"}
            </DialogTitle>
          </DialogHeader>
          <div className="flex-1 min-h-0 overflow-y-auto pl-1 -mr-6 pr-6">
            <DialogDescription className="pb-4">
              {view === "login"
                ? "Ingresa tus datos para continuar."
                : "Únete a cientos de miles de personas que ya realizan su súper semanal en Calii."}
            </DialogDescription>
            {content()}
            {guestButton}
          </div>
        </DialogContent>
      </Dialog>
    );
  }

  // return (
  //   <Drawer open={open} onOpenChange={handleClose} dismissible={dismissable}>
  //     <DrawerContent>
  //       <div className="overflow-y-auto px-4 pb-6">
  //         <DrawerHeader className="px-0">
  //           <DrawerTitle className="text-2xl">
  //             {view === "login" ? "Iniciar sesión" : "Crear cuenta"}
  //           </DrawerTitle>
  //           <DrawerDescription>
  //             {view === "login"
  //               ? "Ingresa tus datos para continuar."
  //               : "Únete a cientos de miles de personas que ya realizan su súper semanal en Calii."}
  //           </DrawerDescription>
  //         </DrawerHeader>
  //         {content(guestButton)}
  //       </div>
  //     </DrawerContent>
  //   </Drawer>
  // );
}
